import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import { ArrowRight, Calendar, MapPin, Clock, CheckCircle } from '../components/icons';
import { getEventById } from '../data/events';
import { api } from '../lib/api';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;         // WhatsApp / contact number
  paymentPhone: string;  // mobile money number used for the ClickPesa charge
  company: string;
  specialization: string;
  cpdNumber: string;
  yearsOfExperience: string;
  comments: string;
}

const emptyForm: FormState = {
  firstName: '', lastName: '', email: '', phone: '', paymentPhone: '',
  company: '', specialization: '', cpdNumber: '', yearsOfExperience: '', comments: '',
};

const PROFESSIONS = [
  'General Dentist',
  'Dental Surgeon (BDS/DDS)',
  'Prosthodontist',
  'Orthodontist',
  'Endodontist',
  'Periodontist',
  'Oral & Maxillofacial Surgeon',
  'Pediatric Dentist',
  'Dental Therapist',
  'Dental Hygienist',
  'Dental Technician',
  'Dental Assistant / Nurse',
  'Dental Student',
  'Other',
];

type Phase = 'form' | 'paying' | 'success' | 'failed' | 'timeout';

// Manual (M-Pesa) payment details, shown while online payment is unavailable.
const MPESA = { number: '+255 754 690 738', name: 'Dentopharma Supplies Limited' };

const fmtTZS = (n: number) => new Intl.NumberFormat('en-TZ').format(n);

export default function EventRegister() {
  const { id } = useParams<{ id: string }>();
  const event = id ? getEventById(id) : undefined;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('form');
  const [doneKind, setDoneKind] = useState<'paid' | 'registered' | 'already'>('registered');
  const [orderRef, setOrderRef] = useState<string | null>(null);
  const [voucher, setVoucher] = useState<{ code: string; value?: number } | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  // Whether the online payment gateway is live. Defaults to false so the form
  // works as a manual registration until the API says online payment is on
  // (then the mobile-money section reappears automatically, no code change).
  const [onlinePayment, setOnlinePayment] = useState(false);

  const fee = event?.fee ?? 0;
  const isPaid = fee > 0;
  const manualMode = isPaid && !onlinePayment; // register now, pay by M-Pesa

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [id]);

  // Ask the API whether online payment is available for this event.
  useEffect(() => {
    if (!event?.slug) return;
    let cancelled = false;
    api.get(`/events/slug/${event.slug}`)
      .then((res) => { if (!cancelled) setOnlinePayment(res.data?.onlinePayment === true); })
      .catch(() => { /* keep manual mode on error */ });
    return () => { cancelled = true; };
  }, [event?.slug]);

  // Poll the payment status while a mobile-money prompt is being completed.
  useEffect(() => {
    if (phase !== 'paying' || !orderRef || !event?.slug) return;
    let stopped = false;
    let tries = 0;
    const tick = async () => {
      tries++;
      try {
        const res = await api.get(`/events/payment-status/${orderRef}`);
        const st = res.data?.paymentStatus;
        if (st === 'PAID') {
          if (res.data?.voucherCode) setVoucher({ code: res.data.voucherCode, value: res.data.voucherValue });
          setDoneKind('paid'); setPhase('success'); return;
        }
        if (st === 'FAILED') { setError(res.data?.message || 'The payment failed or was cancelled.'); setPhase('failed'); return; }
      } catch { /* keep polling */ }
      if (stopped) return;
      if (tries >= 45) { setPhase('timeout'); return; } // ~3 minutes
      timer = setTimeout(tick, 4000);
    };
    let timer = setTimeout(tick, 4000);
    return () => { stopped = true; clearTimeout(timer); };
  }, [phase, orderRef, event?.slug]);

  // DEV-ONLY preview: `?demoVoucher=1` on the dev server jumps straight to the
  // paid confirmation screen with a sample voucher, so the QR/message can be
  // reviewed without a real payment. Guarded by import.meta.env.DEV — it is
  // stripped from production builds and never runs on the live site.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (new URLSearchParams(window.location.search).get('demoVoucher')) {
      setDoneKind('paid');
      setVoucher({ code: 'DP-DEMO12', value: fee || 70000 });
      setPhase('success');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the voucher QR (encodes the staff redeem URL, same as the email) so
  // the delegate can screenshot it straight from the confirmation screen.
  useEffect(() => {
    if (!voucher?.code) { setQrUrl(null); return; }
    const target = `${window.location.origin}/admin/events/redeem?code=${encodeURIComponent(voucher.code)}`;
    QRCode.toDataURL(target, { width: 320, margin: 1 }).then(setQrUrl).catch(() => setQrUrl(null));
  }, [voucher?.code]);

  // Event doesn't exist, isn't upcoming, or registration isn't open.
  if (!event || !event.upcoming || !event.registrationOpen || !event.slug) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 text-center pt-24">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Registration unavailable</h1>
        <p className="text-gray-500 mb-6">This event isn’t open for registration.</p>
        <Link to="/events" className="inline-flex items-center gap-2 bg-[#0D56A6] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0A4A8F] transition-colors">
          Back to Events <ArrowRight size={16} />
        </Link>
      </div>
    );
  }

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.firstName.trim()) return setError('Please enter your first name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError('Please enter a valid email address.');
    if (!form.phone.trim()) return setError('Please enter your WhatsApp phone number.');
    if (isPaid && onlinePayment && !form.paymentPhone.trim()) return setError('Please enter the mobile money number to pay with.');

    try {
      setSubmitting(true);
      const res = await api.post(`/events/slug/${event.slug}/register`, form);
      const data = res.data || {};
      if (data.success && data.paymentStatus === 'PROCESSING' && data.orderReference) {
        setOrderRef(data.orderReference);
        setPhase('paying');
      } else if (data.success && data.manual) {
        // Manual (pay-by-M-Pesa) registration: no online charge.
        setDoneKind(data.alreadyRegistered ? 'already' : 'registered');
        setPhase('success');
      } else if (data.success && (data.paymentStatus === 'PAID' || !isPaid)) {
        setDoneKind(data.alreadyRegistered ? 'already' : (isPaid ? 'paid' : 'registered'));
        setPhase('success');
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not submit your registration. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => { setError(null); setOrderRef(null); setPhase('form'); };

  const inputClass = 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D56A6] focus:border-transparent outline-none transition';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Compact hero: uses the event banner as a soft, brand-tinted backdrop */}
      <section data-hero className="relative overflow-hidden bg-[#062f63] text-white pt-32 pb-14">
        {event.bannerImage && (
          <img src={event.bannerImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-110 blur-[6px]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0D56A6]/90 via-[#0a4690]/88 to-[#062f63]/94" />
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#8EC641]/10 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <span className="inline-block bg-[#8EC641] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide mb-4">
            Event Registration
          </span>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-4 text-white">{event.title}</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/85">
            <span className="flex items-center gap-2"><Calendar size={16} className="text-[#8EC641]" /> {event.date}</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-[#8EC641]" /> {event.time}</span>
            <span className="flex items-center gap-2"><MapPin size={16} className="text-[#8EC641]" /> {event.location}</span>
          </div>
          {isPaid && (
            <div className="mt-5 inline-flex items-baseline gap-2 rounded-xl bg-white/10 border border-white/20 px-4 py-2">
              <span className="text-white/70 text-sm">Entrance fee</span>
              <span className="text-2xl font-extrabold text-white">TZS {fmtTZS(fee)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {phase === 'success' ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
            <div className="flex justify-center mb-4 text-[#8EC641]"><CheckCircle size={56} /></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {doneKind === 'already' ? 'You’re already registered' : doneKind === 'paid' ? 'Payment received, you’re registered!' : 'Registration received!'}
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-8">
              {doneKind === 'already'
                ? 'Our records show this email is already registered for the event. We’ll be in touch with full details closer to the date.'
                : doneKind === 'paid'
                ? `Thank you. Your TZS ${fmtTZS(fee)} payment was confirmed and your place is booked. We’ll email and WhatsApp you the final venue and programme details.`
                : manualMode
                ? 'Thanks for registering. Your place is reserved. To confirm your seat, please complete the entrance fee payment below.'
                : 'Thank you for registering. We’ll confirm your place and share the final details by email and WhatsApp closer to the date.'}
            </p>

            {manualMode && doneKind === 'registered' && (
              <div className="max-w-sm mx-auto mb-8 text-left">
                <div className="rounded-2xl border-2 border-dashed border-[#0D56A6] bg-[#eef5fc] p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Complete your payment</p>
                  <p className="text-sm text-gray-700 mb-3">
                    Pay the <strong>TZS {fmtTZS(fee)}</strong> entrance fee via M-Pesa to secure your seat:
                  </p>
                  <div className="rounded-lg bg-white border border-gray-200 p-4 text-center mb-3">
                    <p className="text-xs text-gray-400">M-Pesa number</p>
                    <p className="text-xl font-extrabold text-[#0D56A6]">{MPESA.number}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{MPESA.name}</p>
                  </div>
                  <p className="text-xs text-gray-500">
                    Use your full name as the reference, then send your M-Pesa confirmation SMS to {MPESA.number} on WhatsApp and we’ll confirm your seat.
                  </p>
                </div>
              </div>
            )}

            {voucher && (
              <div className="max-w-sm mx-auto mb-8">
                <div className="rounded-2xl border-2 border-dashed border-[#8EC641] bg-[#f6fbef] p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Your event voucher</p>
                  {qrUrl && (
                    <img
                      src={qrUrl}
                      alt="Voucher QR code"
                      className="w-44 h-44 mx-auto mt-3 rounded-lg border border-gray-200 bg-white p-2"
                    />
                  )}
                  <p className="text-2xl font-extrabold tracking-widest text-[#0D56A6] mt-3">{voucher.code}</p>
                  <p className="text-sm text-[#4b8b1f] font-medium mt-1">
                    Worth TZS {fmtTZS(voucher.value ?? fee)}
                  </p>
                  <p className="text-xs text-gray-500 mt-3">
                    Show this QR code at the entrance and our team will scan it to claim your voucher against the Composite Kit or the Implant System Kit. We’ve also emailed you a copy. It can be used once.
                  </p>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/events/${event.id}`} className="inline-flex items-center justify-center gap-2 bg-[#0D56A6] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#0A4A8F] transition-colors">
                View Programme <ArrowRight size={16} />
              </Link>
              <Link to="/events" className="inline-flex items-center justify-center border-2 border-[#0D56A6] text-[#0D56A6] font-semibold px-6 py-3 rounded-lg hover:bg-[#0D56A6] hover:text-white transition-colors">
                All Events
              </Link>
            </div>
          </motion.div>
        ) : phase === 'paying' ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
            <div className="flex justify-center mb-5">
              <span className="inline-block w-12 h-12 border-4 border-[#8EC641] border-t-transparent rounded-full animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Check your phone</h2>
            <p className="text-gray-600 max-w-md mx-auto mb-2">
              We’ve sent a payment request for <strong>TZS {fmtTZS(fee)}</strong> to <strong>{form.paymentPhone || form.phone}</strong>.
            </p>
            <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
              Enter your mobile money PIN on the prompt to complete payment. This page will update automatically once payment is confirmed, so please don’t close it.
            </p>
            <button onClick={retry} className="text-sm text-gray-400 hover:text-gray-600 underline">Cancel</button>
          </div>
        ) : phase === 'failed' || phase === 'timeout' ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 sm:p-10 text-center">
            <div className="text-5xl mb-4">{phase === 'failed' ? '⚠️' : '⏳'}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {phase === 'failed' ? 'Payment not completed' : 'Still waiting for payment'}
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              {phase === 'failed'
                ? (error || 'The payment failed or was cancelled.') + ' You can try again.'
                : 'We haven’t received confirmation yet. If you completed the payment, it may take a moment. Otherwise please try again.'}
            </p>
            <button onClick={retry} className="inline-flex items-center justify-center gap-2 bg-[#8EC641] hover:bg-[#7ab535] text-white font-semibold px-6 py-3 rounded-lg transition-colors">
              Try again <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-1">Reserve your place</h2>
            <p className="text-gray-500 text-sm mb-6">
              {manualMode
                ? `The entrance fee is TZS ${fmtTZS(fee)} and covers the full-day programme, lunch and refreshments. Register below, then pay by M-Pesa to secure your seat. Places are limited and allocated first-come, first-served. Fields marked * are required.`
                : isPaid
                ? `The entrance fee is TZS ${fmtTZS(fee)}, paid by mobile money, and covers the full-day programme, lunch and refreshments. Places are limited and allocated first-come, first-served. Fields marked * are required.`
                : 'Places are limited and allocated first-come, first-served. Fields marked * are required.'}
            </p>

            {error && (
              <div className="mb-5 rounded-lg bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>First name *</label>
                  <input className={inputClass} value={form.firstName} onChange={set('firstName')} placeholder="Jane" />
                </div>
                <div>
                  <label className={labelClass}>Last name</label>
                  <input className={inputClass} value={form.lastName} onChange={set('lastName')} placeholder="Doe" />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Email *</label>
                  <input type="email" className={inputClass} value={form.email} onChange={set('email')} placeholder="jane@clinic.co.tz" />
                </div>
                <div>
                  <label className={labelClass}>Phone (WhatsApp) *</label>
                  <input className={inputClass} value={form.phone} onChange={set('phone')} placeholder="+255 7XX XXX XXX" />
                  <p className="text-xs text-gray-400 mt-1.5">We’ll send event updates here.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Practice / Clinic</label>
                  <input className={inputClass} value={form.company} onChange={set('company')} placeholder="XYZ Dental Clinic" />
                </div>
                <div>
                  <label className={labelClass}>Profession / Specialization</label>
                  <select className={inputClass} value={form.specialization} onChange={set('specialization')}>
                    <option value="">Select…</option>
                    {PROFESSIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Licence number</label>
                  <input className={inputClass} value={form.cpdNumber} onChange={set('cpdNumber')} placeholder="e.g. MCT382201" />
                  <p className="text-xs text-gray-400 mt-1.5">Required to issue your CPD certificate.</p>
                </div>
                <div>
                  <label className={labelClass}>Years of experience</label>
                  <select className={inputClass} value={form.yearsOfExperience} onChange={set('yearsOfExperience')}>
                    <option value="">Prefer not to say</option>
                    <option value="Student">Student</option>
                    <option value="0-2 years">0-2 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="6-10 years">6-10 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Anything you’d like us to know?</label>
                <textarea rows={3} className={inputClass} value={form.comments} onChange={set('comments')} placeholder="Questions, dietary requirements, workshop preference…" />
              </div>

              {isPaid && onlinePayment && (
                <div className="rounded-xl border border-[#8EC641]/50 bg-[#8EC641]/5 p-5">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-gray-900">Entrance fee</h3>
                    <span className="text-lg font-extrabold text-[#0D56A6]">TZS {fmtTZS(fee)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Includes the full-day CPD programme, lunch and refreshments.</p>
                  <label className={labelClass}>Mobile money number *</label>
                  <input className={inputClass} value={form.paymentPhone} onChange={set('paymentPhone')} placeholder="0754 000 000" />
                  <p className="text-xs text-gray-500 mt-1.5">
                    Enter the M-Pesa / Tigo Pesa / Airtel Money number to pay with. You’ll get a PIN prompt on this number to confirm TZS {fmtTZS(fee)}. (Can be the same as your WhatsApp number.)
                  </p>
                </div>
              )}

              {manualMode && (
                <p className="text-sm text-gray-500 text-center">
                  You’ll get the M-Pesa payment details right after you register.
                </p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#8EC641] hover:bg-[#7ab535] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold px-6 py-3.5 rounded-lg transition-colors"
              >
                {submitting ? 'Please wait…' : (isPaid && onlinePayment) ? `Pay TZS ${fmtTZS(fee)} & Register` : 'Complete Registration'}
                {!submitting && <ArrowRight size={16} />}
              </button>

              <p className="text-xs text-gray-400 text-center">
                By registering you agree to be contacted with event details. Your information is used only for this event.
              </p>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
